import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  DataList,
  PageHeader,
  RowActions,
  StatusPill,
  useActionRunner,
  type DataListColumn,
} from "@ozpanel/kit";
import { useQuery as useQ } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Button } from "@ozpanel/ui";
import { useCreate } from "../../useCreate";
import { useAccountsApi, type Backend } from "../../accountsApi";
import { appActions } from "./appActions";

function liveText(b: Backend): string {
  if (b.live) return b.live.summary;
  return b.active ? "running (this node)" : "stopped";
}

const columns: DataListColumn<Backend>[] = [
  { key: "name", label: "App", sortable: true },
  { key: "domain", label: "Domain", sortable: true },
  { key: "tenant_name", label: "Tenant", sortable: true, value: (b) => b.tenant_name || "—" },
  { key: "runtime", label: "Runtime", sortable: true },
  {
    key: "placement",
    label: "Placement",
    value: (b) => `${b.ha_mode || "process"} ${b.nodes?.join(",") ?? ""}`,
    render: (b) => `${b.ha_mode || "process"} (${b.nodes?.join(", ") || "this node"})`,
  },
  {
    key: "enabled",
    label: "Desired",
    sortable: true,
    value: (b) => (b.enabled ? "enabled" : "disabled"),
    render: (b) => (
      <StatusPill tone={b.enabled ? "ok" : "neutral"}>{b.enabled ? "enabled" : "disabled"}</StatusPill>
    ),
  },
  {
    key: "live",
    label: "Live",
    value: liveText,
    render: (b) => (
      <StatusPill tone={b.live ? (b.live.healthy ? "ok" : "bad") : b.active ? "ok" : "neutral"}>
        {liveText(b)}
      </StatusPill>
    ),
  },
];

export function AppsScreen() {
  const api = useAccountsApi();
  const apps = useQuery({ queryKey: ["apps"], queryFn: api.listApps });
  const navigate = useNavigate();
  const runner = useActionRunner<Backend>();
  const actions = appActions(api);
  const domains = useQ({
    queryKey: ["domains"],
    queryFn: () => api.listDomains(),
    retry: false,
  });
  const placementNodes = useQ({
    queryKey: ["placement-nodes"],
    queryFn: () => api.listPlacementNodes(),
    retry: false,
  });
  const create = useCreate({
    title: "New app backend",
    description:
      "Runs a process or container behind one of this account's domains. The web vhost is bound to it automatically.",
    submitLabel: "Create app",
    fields: [
      { key: "name", label: "Name", required: true, placeholder: "acme-api" },
      {
        key: "domain",
        label: "Domain",
        required: true,
        help: "The hostname this app is served at.",
        options: (domains.data?.items ?? []).map((d) => ({ value: d.fqdn, label: d.fqdn })),
      },
      {
        key: "runtime",
        label: "Runtime",
        options: [
          { value: "process", label: "process — a command run by systemd" },
          { value: "container", label: "container — a published image" },
        ],
        defaultValue: "process",
      },
      { key: "image", label: "Image", placeholder: "traefik/whoami:latest", help: "Container runtime only." },
      { key: "command", label: "Command", placeholder: "/usr/bin/node server.js", help: "Process runtime only." },
      { key: "working_dir", label: "Working directory", placeholder: "/home/<user>/app" },
      { key: "port", label: "Port", type: "int", required: true, placeholder: "3000" },
      {
        key: "placement",
        label: "Where it runs",
        help: "All nodes stays up if one goes down; a pinned node does not move if that node fails.",
        options: [
          { value: "all", label: "All nodes" },
          ...(placementNodes.data?.nodes ?? []).map((n) => ({ value: n, label: `Only ${n}` })),
        ],
        defaultValue: placementNodes.data?.this_node || "all",
      },
    ],
    run: (v) => {
      const place =
        !v.placement || v.placement === "all"
          ? { ha_mode: "replicated", nodes: [] as string[] }
          : { ha_mode: "singleton", nodes: [String(v.placement)] };
      return api.createApp({
        name: v.name,
        domain: v.domain,
        runtime: v.runtime || "process",
        image: v.image || "",
        command: v.command || "",
        working_dir: v.working_dir || "",
        port: Number(v.port),
        ...place,
      });
    },
    invalidates: [["apps"]],
    success: (v) => `App ${v.name} created`,
  });

  return (
    <div>
      <PageHeader
        title="App backends"
        subtitle={
          apps.data ? `${apps.data.apps.length} apps · this node: ${apps.data.this_node}` : undefined
        }
        actions={
          <Button variant="accent" onClick={create.open}>
            <Plus size={14} />
            New app
          </Button>
        }
      />
      <DataList
        items={apps.data?.apps ?? []}
        columns={columns}
        getRowKey={(b) => String(b.id)}
        onRowClick={(b) => navigate(`/apps/${encodeURIComponent(b.name)}`)}
        rowActions={(b) => <RowActions actions={actions} obj={b} runner={runner} />}
        searchable
        searchPlaceholder="Search apps, domains, tenants…" loading={apps.isLoading}
        error={apps.error}
        errorContext="app backends" emptyMessage="No app backends yet." />
      {runner.dialog}
      {create.dialog}
    </div>
  );
}
